import { memo, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { getEventHubUrl, getViewedProfileUserName, hubRouteTarget } from "./eventMenuConfig";

const resolveEventHeroImage = () => {
  const publicBase = String(process.env.PUBLIC_URL || "/event").replace(/\/+$/, "");
  const path = `${publicBase}/assets/img/danzflo-events.jpg`;
  if (typeof window !== "undefined" && path.startsWith("/")) {
    return `${window.location.origin}${path}`;
  }
  return path;
};

const HERO_TABS = [
  { id: "event-list", label: "Event list", icon: "bi-list", to: hubRouteTarget("/") },
  // { id: "calendar", label: "Calendar", icon: "bi-calendar3", to: hubRouteTarget("/schedule") },
  { id: "my-events", label: "My Events", icon: "bi-ticket-perforated", to: hubRouteTarget("/MyEvents") },
  { id: "wishlist", label: "Wishlist", icon: "bi-heart", to: hubRouteTarget("/wishlist") },
];

const EventV2HeroBanner = ({
  activeId,
  onOpenMenu,
  title = "Events",
  subtitle,
  eyebrow = "DanzFlo events hub",
}) => {
  const location = useLocation();
  const resolvedActive =
    activeId ||
    HERO_TABS.find((tab) => {
      const tabPath =
        typeof tab.to === "string" ? tab.to : tab.to?.pathname;
      return tabPath === "/"
        ? location.pathname === "/"
        : location.pathname.startsWith(tabPath);
    })?.id ||
    "event-list";

  const scrollToCatalog = (e) => {
    e.preventDefault();
    const target =
      document.getElementById("event-main-scroll") ||
      document.getElementById("event-catalog") ||
      document.querySelector(".event-main-panel");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const heroImage = useMemo(() => resolveEventHeroImage(), []);

  return (
    <section
      className="merch-hero event-hero-banner event-hero-banner--listing"
      aria-label="Events hero"
    >
      <div className="merch-hero-accent" aria-hidden="true" />
      <img
        className="merch-hero-bg event-hero-bg-media"
        src={heroImage}
        alt=""
        aria-hidden="true"
        decoding="async"
        fetchPriority="high"
      />
      <div className="merch-hero-overlay" aria-hidden="true" />
      <div className="merch-hero-inner">
        <div className="merch-hero-layout">
          <div className="merch-hero-copy">
            <div className="merch-eyebrow">
              <i className="bi bi-calendar2-event" aria-hidden="true"></i>
              {eyebrow}
            </div>
            <h1 className="merch-title">
              <span className="text-gradient">{title}</span>
            </h1>
            {subtitle && <p className="merch-sub">{subtitle}</p>}
            <ul className="merch-hero-meta" aria-label="Events highlights">
              <li>
                <i className="bi bi-calendar-week" aria-hidden="true"></i>
                Upcoming events
              </li>
              <li>
                <i className="bi bi-ticket-perforated" aria-hidden="true"></i>
                Ticket sales
              </li>
              <li>
                <i className="bi bi-geo-alt" aria-hidden="true"></i>
                All cities
              </li>
            </ul>
          </div>

          <div className="merch-hero-panel">
            <div className="merch-hero-panel-label">Start exploring</div>
            <div className="merch-hero-actions">
              <a
                className="btn btn-accent btn-lg px-4 merch-hero-cta-primary"
                href="#event-catalog"
                onClick={scrollToCatalog}
              >
                <i className="bi bi-calendar2-event me-2" aria-hidden="true" />
                Browse events
              </a>
              <button
                className="btn btn-outline-light px-4 merch-hero-cta-secondary"
                type="button"
                onClick={() => onOpenMenu && onOpenMenu()}
              >
                <i className="bi bi-grid-3x3-gap me-2" aria-hidden="true" />
                Menu
              </button>
            </div>
          </div>
        </div>

        <div className="merch-hero-tabs-bar">
          <div
            className="merch-hero-tabs"
            role="navigation"
            aria-label="Events sections"
          >
            {HERO_TABS.map((tab) => {
              const tabPath =
                typeof tab.to === "string" ? tab.to : tab.to?.pathname || "/";
              return (
                <Link
                  key={tab.id}
                  className={`mhtab${resolvedActive === tab.id ? " is-active" : ""}`}
                  to={tab.to}
                  onClick={(e) => {
                    if (
                      /\/[^/]+\/(userEvents|userPromotingEvents)(?:\/|$)/.test(
                        location.pathname
                      )
                    ) {
                      e.preventDefault();
                      const viewedUserName = getViewedProfileUserName(
                        location.pathname,
                        location.search
                      );
                      const search =
                        tab.id === "event-list" && viewedUserName
                          ? `?userName=${encodeURIComponent(viewedUserName)}`
                          : "";
                      window.location.assign(
                        getEventHubUrl(tabPath, search)
                      );
                    }
                  }}
                >
                  <i className={`bi ${tab.icon}`} aria-hidden="true"></i>
                  <span>{tab.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default memo(EventV2HeroBanner);
